import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, X, ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import { collection, getDocs, query, where, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import SiteLayout from '../components/SiteLayout';
import { Button } from '../components/ui/button';
import { useBrandSettings } from '../contexts/BrandSettingsContext';
import { useCMSContent } from '../hooks/useCMSContent';

const PricingPage = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showZAR, setShowZAR] = useState(false);
  const { brandName } = useBrandSettings();
  const { content: cmsContent, loading: cmsLoading } = useCMSContent('pricing-page');

  // Fetch plans from Platform Pricing document
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        
        // Pull packages from /platform/pricing document
        // This creates tight coupling to platform configuration as requested
        const pricingRef = doc(db, 'platform', 'pricing');
        const pricingSnap = await getDoc(pricingRef);
        
        if (pricingSnap.exists() && pricingSnap.data().packages) {
          const fetchedPlans = pricingSnap.data().packages;
          // Sort by sortOrder if available
          fetchedPlans.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
          setPlans(fetchedPlans);
        } else {
          // Fallback to legacy collection if platform doc doesn't exist yet
          console.warn('Platform pricing not found, falling back to legacy plans collection');
          const plansRef = collection(db, 'plans');
          const q = query(
            plansRef,
            where('isActive', '==', true),
            orderBy('sortOrder', 'asc')
          );
          const snapshot = await getDocs(q);
          const fetchedPlans = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setPlans(fetchedPlans);
        }
        
        setError(null);
      } catch (err) {
        console.error('Failed to fetch plans:', err);
        setError('Failed to load pricing plans');
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  // Format price with currency
  const formatPrice = (plan) => {
    if (showZAR && plan.priceZAR) {
      const price = billingCycle === 'yearly' 
        ? Math.round(plan.priceZAR * 10) // ~17% discount for yearly
        : plan.priceZAR;
      return `R${price.toLocaleString()}`;
    }
    const price = billingCycle === 'yearly' 
      ? Math.round(plan.price * 10) // ~17% discount for yearly
      : plan.price;
    return `$${price.toLocaleString()}`;
  };

  // Get limit display value
  const getLimitDisplay = (value, suffix = '') => {
    if (value === -1) return 'Unlimited';
    return `${value}${suffix}`;
  };

  // Build feature list from plan data
  const getFeatureList = (plan) => {
    const features = [];
    
    // Users
    if (plan.limits?.users) {
      features.push({
        label: plan.limits.users === -1 ? 'Unlimited users' : `Up to ${plan.limits.users} users`,
        included: true
      });
    }
    
    // ISO Standards
    if (plan.limits?.isoStandards) {
      features.push({
        label: plan.limits.isoStandards === -1 ? 'All ISO standards' : `${plan.limits.isoStandards} ISO standards`,
        included: true
      });
    }
    
    // Storage
    if (plan.limits?.storageGB) {
      features.push({
        label: plan.limits.storageGB === -1 ? 'Unlimited storage' : `${plan.limits.storageGB} GB storage`,
        included: true
      });
    }
    
    // Sites
    if (plan.limits?.sites) {
      features.push({
        label: plan.limits.sites === -1 ? 'Unlimited sites' : plan.limits.sites === 1 ? 'Single site' : `${plan.limits.sites} sites`,
        included: true
      });
    }
    
    // Capabilities
    if (plan.capabilities) {
      if (plan.capabilities.prioritySupport || plan.capabilities.dedicatedSupport) {
        features.push({
          label: plan.capabilities.dedicatedSupport ? 'Dedicated support' : 'Priority support',
          included: true
        });
      } else {
        features.push({ label: 'Email support', included: true });
      }
      
      features.push({ label: 'API Access', included: !!plan.capabilities.apiAccess });
      features.push({ label: 'Custom Templates', included: !!plan.capabilities.customTemplates });
      features.push({ label: 'Advanced Reporting', included: !!plan.capabilities.advancedReporting });
      features.push({ label: 'White Label', included: !!plan.capabilities.whiteLabel });
      features.push({ label: 'SLA Guarantee', included: !!plan.capabilities.slaGuarantee });
    }
    
    return features;
  };

  // Also use plan.features array if available
  const getPlanFeatures = (plan) => {
    if (plan.features && Array.isArray(plan.features)) {
      return plan.features.map(f => ({ label: f, included: true }));
    }
    return getFeatureList(plan);
  };

  // Loading state
  if (loading || cmsLoading) {
    return (
      <SiteLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </SiteLayout>
    );
  }

  // Error state for plans loading
  if (error) {
    return (
      <SiteLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-500 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>Try Again</Button>
          </div>
        </div>
      </SiteLayout>
    );
  }

  // CMS content - no fallbacks, must be configured in CMS
  const hero = cmsContent?.hero;
  const billingToggle = cmsContent?.billingToggle;
  const cta = cmsContent?.cta;

  // Show error if CMS content is not configured
  if (!hero || !billingToggle || !cta) {
    return (
      <SiteLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center max-w-md">
            <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">CMS Content Required</h2>
            <p className="text-muted-foreground mb-4">
              Pricing page content is not configured. Please add content to the CMS document: <code className="bg-muted px-1 rounded">cms_content/pricing-page</code>
            </p>
            <p className="text-sm text-muted-foreground">
              Required fields: hero, billingToggle, cta
            </p>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <div className="container-wide text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {hero.title}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            {hero.subtitle}
          </p>

          {/* Controls */}
          <div className="flex flex-wrap items-center justify-center gap-4 mb-4">
            {/* Billing Toggle */}
            <div className="inline-flex items-center gap-4 p-1 bg-muted rounded-lg">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  billingCycle === 'monthly' ? 'bg-white shadow text-foreground' : 'text-muted-foreground'
                }`}
              >
                {billingToggle.monthly}
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  billingCycle === 'yearly' ? 'bg-white shadow text-foreground' : 'text-muted-foreground'
                }`}
              >
                {billingToggle.yearly}
                {billingToggle.yearlySavings && (
                  <span className="ml-2 text-xs text-green-600 font-semibold">{billingToggle.yearlySavings}</span>
                )}
              </button>
            </div>

            {/* Currency Toggle */}
            {plans.some(p => p.priceZAR) && (
              <div className="inline-flex items-center gap-4 p-1 bg-muted rounded-lg">
                <button
                  onClick={() => setShowZAR(false)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    !showZAR ? 'bg-white shadow text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  USD $
                </button>
                <button
                  onClick={() => setShowZAR(true)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    showZAR ? 'bg-white shadow text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  ZAR R
                </button>
              </div>
            )}
          </div>

          {/* Exchange rate info */}
          {showZAR && plans[0]?.exchangeRate && (
            <p className="text-xs text-muted-foreground">
              Exchange rate: 1 USD = R{plans[0].exchangeRate.toFixed(2)}
            </p>
          )}
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20">
        <div className="container-wide">
          <div className={`grid gap-8 max-w-6xl mx-auto ${
            plans.length === 3 ? 'md:grid-cols-3' : 
            plans.length === 2 ? 'md:grid-cols-2 max-w-4xl' : 
            'md:grid-cols-1 max-w-lg'
          }`}>
            {plans.map((plan) => {
              const period = billingCycle === 'monthly' ? '/mo' : '/yr';
              const features = getPlanFeatures(plan);

              return (
                <div 
                  key={plan.id}
                  className={`relative bg-card border rounded-2xl p-8 ${
                    plan.isPopular ? 'border-primary shadow-xl scale-105 z-10' : 'border-border'
                  }`}
                >
                  {plan.isPopular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </div>
                  )}

                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <p className="text-muted-foreground text-sm mb-6">{plan.description}</p>

                  <div className="mb-2">
                    <span className="text-4xl font-bold">{formatPrice(plan)}</span>
                    <span className="text-muted-foreground">{period}</span>
                  </div>
                  
                  {/* Show both currencies */}
                  {!showZAR && plan.priceZAR && (
                    <p className="text-sm text-muted-foreground mb-6">
                      or R{(billingCycle === 'yearly' ? plan.priceZAR * 10 : plan.priceZAR).toLocaleString()}{period}
                    </p>
                  )}
                  {showZAR && (
                    <p className="text-sm text-muted-foreground mb-6">
                      or ${(billingCycle === 'yearly' ? plan.price * 10 : plan.price).toLocaleString()}{period}
                    </p>
                  )}

                  {plan.trialDays && (
                    <p className="text-xs text-green-600 font-medium mb-4">
                      {plan.trialDays}-day free trial
                    </p>
                  )}

                  <Link to={`/signup?plan=${plan.id}`}>
                    <Button 
                      className="w-full mb-8" 
                      variant={plan.isPopular ? 'default' : 'outline'}
                    >
                      Start Free Trial
                    </Button>
                  </Link>

                  <ul className="space-y-3">
                    {features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        {feature.included ? (
                          <Check size={18} className="text-green-500 mt-0.5 flex-shrink-0" />
                        ) : (
                          <X size={18} className="text-gray-300 mt-0.5 flex-shrink-0" />
                        )}
                        <span className={feature.included ? '' : 'text-muted-foreground'}>
                          {feature.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      {plans.length > 1 && (
        <section className="py-20 bg-muted/50">
          <div className="container-wide">
            <h2 className="text-3xl font-bold text-center mb-12">Compare all features</h2>

            <div className="overflow-x-auto">
              <table className="w-full max-w-5xl mx-auto">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-4 px-4">Feature</th>
                    {plans.map((plan) => (
                      <th key={plan.id} className="text-center py-4 px-4">
                        {plan.name}
                        {plan.isPopular && (
                          <span className="block text-xs text-primary font-normal">Most Popular</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Limits */}
                  <tr className="border-b bg-muted/30">
                    <td colSpan={plans.length + 1} className="py-2 px-4 font-semibold text-sm text-muted-foreground">
                      Limits
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-4 px-4 font-medium">Users</td>
                    {plans.map((plan) => (
                      <td key={plan.id} className="text-center py-4 px-4 text-sm">
                        {getLimitDisplay(plan.limits?.users || 0)}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b">
                    <td className="py-4 px-4 font-medium">ISO Standards</td>
                    {plans.map((plan) => (
                      <td key={plan.id} className="text-center py-4 px-4 text-sm">
                        {plan.limits?.isoStandards === -1 ? 'All' : plan.limits?.isoStandards || 0}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b">
                    <td className="py-4 px-4 font-medium">Storage</td>
                    {plans.map((plan) => (
                      <td key={plan.id} className="text-center py-4 px-4 text-sm">
                        {getLimitDisplay(plan.limits?.storageGB || 0, ' GB')}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b">
                    <td className="py-4 px-4 font-medium">Sites</td>
                    {plans.map((plan) => (
                      <td key={plan.id} className="text-center py-4 px-4 text-sm">
                        {getLimitDisplay(plan.limits?.sites || 1)}
                      </td>
                    ))}
                  </tr>
                  
                  {/* Capabilities */}
                  <tr className="border-b bg-muted/30">
                    <td colSpan={plans.length + 1} className="py-2 px-4 font-semibold text-sm text-muted-foreground">
                      Capabilities
                    </td>
                  </tr>
                  {[
                    { key: 'apiAccess', label: 'API Access' },
                    { key: 'customTemplates', label: 'Custom Templates' },
                    { key: 'advancedReporting', label: 'Advanced Reporting' },
                    { key: 'customIntegrations', label: 'Custom Integrations' },
                    { key: 'whiteLabel', label: 'White Label' },
                    { key: 'slaGuarantee', label: 'SLA Guarantee' },
                    { key: 'prioritySupport', label: 'Priority Support' },
                    { key: 'dedicatedSupport', label: 'Dedicated Support' },
                  ].map((cap) => (
                    <tr key={cap.key} className="border-b">
                      <td className="py-4 px-4 font-medium">{cap.label}</td>
                      {plans.map((plan) => (
                        <td key={plan.id} className="text-center py-4 px-4">
                          {plan.capabilities?.[cap.key] ? (
                            <Check size={18} className="text-green-500 mx-auto" />
                          ) : (
                            <X size={18} className="text-gray-300 mx-auto" />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-primary text-white">
        <div className="container-wide text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {cta.title}
          </h2>
          <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
            {cta.subtitle}
          </p>
          <Link to={cta.href}>
            <Button size="xl" className="bg-white text-primary hover:bg-white/90 gap-2">
              {cta.button}
              <ArrowRight size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
};

export default PricingPage;
